import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import ResultsIcon from "../../assets/all.svg?react";
import CatalogIcon from "../../assets/last-used.svg?react";
import CloseIcon from "../../assets/close.svg?react";

import { AppContext } from "../../Context";
import { StoreState } from "../../store/rootReducer";
import { ItemDisplayType, setSearchItemDisplayType } from "../../store/searchSlice";
import Catalog from "./Catalog";
import ClickResultsContent from "./ClickResultsContent";
import "./SearchResults.css";
import SearchResultsContent from "./SearchResultsContent";
import { setLeftPanelActiveTab } from "../../store/appSlice";
import { LeftPanelTabs } from "../../constants/Panels";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

enum TabIndex {
    Catalog = 0,
    SearchResults = 1,
}

const SearchResults = () => {
    const dispatch = useDispatch();

    const appContext = useContext(AppContext);
    const searchResults = appContext?.searchResults.value ?? [];
    const clickResults = appContext?.clickResults.value ?? [];

    const [selectedIndex, setSelectedIndex] = useState<number>(TabIndex.Catalog);

    const searchItemDisplayType = useSelector((state: StoreState) => state.search.searchItemDisplayType);
    const activeVersionGuid = useSelector((state: StoreState) => state.version.activeGuid);
    const leftPanelActiveTab = useSelector((state: StoreState) => state.app.leftPanelActiveTab);

    const highlightedFeaturesRef = useRef<ResourceHandle[]>([]);

    const clearHighlightedFeatures = () => {
        highlightedFeaturesRef.current.forEach((handle) => {
            handle.remove();
        });
        highlightedFeaturesRef.current = [];
    };

    const storeHighlightHandle = (handle: ResourceHandle | null) => {
        clearHighlightedFeatures();
        if (handle) {
            highlightedFeaturesRef.current.push(handle);
        }
    };

    const closePanel = () => {
        dispatch(setLeftPanelActiveTab(LeftPanelTabs.UNDEFINED));
    }

    useEffect(() => {
        if (searchResults.length > 0 || clickResults.length > 0) {
            setSelectedIndex(TabIndex.SearchResults);
        }
    }, [searchResults, clickResults]);

    useEffect(() => {
        if (searchResults.length === 0) return;
        dispatch(setSearchItemDisplayType(ItemDisplayType.Search));
    }, [searchResults]);

    useEffect(() => {
        if (clickResults.length === 0) return;
        dispatch(setSearchItemDisplayType(ItemDisplayType.Click));
    }, [clickResults]);

    useEffect(() => {
        appContext?.searchResults.set([]);
        appContext?.clickResults.set([]);
    }, [activeVersionGuid]);

    useEffect(() => {
        if (leftPanelActiveTab === LeftPanelTabs.SEARCH) {
            setSelectedIndex(TabIndex.SearchResults);
        } else if (leftPanelActiveTab === LeftPanelTabs.CATALOG) {
            setSelectedIndex(TabIndex.Catalog);
        }
    }, [leftPanelActiveTab]);

    return (
        <div className="search-results">
            <Tabs
                className="h-100 flex-col"
                selectedTabClassName="selected"
                selectedIndex={selectedIndex}
                onSelect={(index) => setSelectedIndex(index)}
            >
                <TabList className="tab-list hidden">
                    <Tab className="tab">
                        <CatalogIcon />
                        <span>Katalog</span>
                    </Tab>
                    <Tab className="tab">
                        <ResultsIcon />
                        <span>Resultat</span>
                    </Tab>
                </TabList>
                <TabPanel className="tab-panel">
                    <div className="w-100 space-between">
                        <span className="search-results-title">Katalog</span>
                        <button className="empty-button" onClick={closePanel}>
                            <CloseIcon />
                        </button>
                    </div>
                    <Catalog
                        storeHighlightHandle={storeHighlightHandle}
                        clearHighlightedFeatures={clearHighlightedFeatures}
                    />
                </TabPanel>
                <TabPanel className="tab-panel">
                    <div className="w-100 space-between">
                        <span className="search-results-title">Resultat</span>
                        <button className="empty-button" onClick={closePanel}>
                            <CloseIcon />
                        </button>
                    </div>
                    {searchItemDisplayType === ItemDisplayType.Click ? (
                        <ClickResultsContent
                            storeHighlightHandle={storeHighlightHandle}
                            clearHighlightedFeatures={clearHighlightedFeatures}
                        />
                    ) : (
                        <SearchResultsContent
                            storeHighlightHandle={storeHighlightHandle}
                            clearHighlightedFeatures={clearHighlightedFeatures}
                        />
                    )}
                </TabPanel>
            </Tabs>
        </div>
    );
};

export default SearchResults;
